// client/src/pages/secure/PhoneNotificationsPage.js — "Phone Notifications" (feature #6).
//
// Each login, on each phone or computer, turns push notifications on or off, names and
// removes its devices, chooses which kinds of event it wants, and sends itself a test.
// The managing broker also sees the office switch (OFF until switched on, per site) and
// the "Generate keys" button. Server: routes/push.js (/api/push); the notification itself
// is shown by client/public/sw.js.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

const btn = 'text-sm px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:opacity-50';
const ghost = 'text-sm px-3 py-1.5 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50';
const card = 'bg-white border border-slate-200 rounded-lg p-5 space-y-3';
const fmt = (d) => (d ? new Date(d).toLocaleString('en-US') : '—');

/** VAPID public key (base64url) -> Uint8Array for pushManager.subscribe. */
function keyBytes(b64u) {
    const pad = '='.repeat((4 - (b64u.length % 4)) % 4);
    const raw = window.atob((b64u + pad).replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

const supported = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
const isIos = () => typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent || '');
const standalone = () => typeof window !== 'undefined'
    && ((window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true);

/** The service worker registration, or null within a few seconds (dev builds have none). */
async function registration() {
    if (!('serviceWorker' in navigator)) return null;
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (existing) return existing;
    return Promise.race([navigator.serviceWorker.ready, new Promise((r) => setTimeout(() => r(null), 4000))]);
}

function OfficeSwitch({ api }) {
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const load = useCallback(async () => {
        try { setS(await api.get('/api/push/admin')); } catch (e) { setMsg(e.message); }
    }, [api]);
    useEffect(() => { load(); }, [load]);
    const act = async (fn) => {
        setBusy(true); setMsg('');
        try { setS(await fn()); } catch (e) { setMsg(e.message); } finally { setBusy(false); }
    };
    if (!s) return msg ? <div className={card}><p className="text-sm text-red-700">{msg}</p></div> : null;
    return (
        <div className={card}>
            <h2 className="text-lg font-semibold text-slate-800">Office switch (managing broker)</h2>
            <p className="text-sm text-slate-600">
                While this is off, the portal sends no automatic phone notifications on this site. People can still turn their
                devices on and send themselves a test. Email and text alerts are not affected either way.
            </p>
            <div className="flex flex-wrap items-center gap-3">
                <span className={`text-sm font-medium ${s.enabled ? 'text-green-700' : 'text-slate-500'}`}>
                    Automatic phone notifications are {s.enabled ? 'ON' : 'OFF'}
                </span>
                <button className={btn} disabled={busy} onClick={() => act(() => api.put('/api/push/admin', { enabled: !s.enabled }))}>
                    {s.enabled ? 'Switch off' : 'Switch on'}
                </button>
            </div>
            <div className="text-sm text-slate-700 space-y-1">
                <div>Server keys: {s.keys_ready ? <span className="text-green-700">ready</span> : <span className="text-amber-700">not created yet</span>}
                    {!s.keys_ready && (
                        <button className={`${ghost} ml-3`} disabled={busy}
                            onClick={() => act(() => api.post('/api/push/admin/generate-keys', {}))}>Generate keys</button>
                    )}
                </div>
                {s.message && <div className="text-amber-700">{s.message}</div>}
                <div>Devices turned on: {s.devices} (across {s.logins} login{s.logins === 1 ? '' : 's'})</div>
                {s.recent && s.recent.length > 0 && (
                    <div>Last 7 days: {s.recent.map((r) => `${r.category} ${r.outcome} ×${r.n}`).join(' · ')}</div>
                )}
                <p className="text-xs text-slate-500">
                    To start over with new keys, clear "Phone push notifications" at Admin &amp; Settings → Integrations; every device
                    must then be turned on again.
                </p>
            </div>
            {msg && <p className="text-sm text-red-700">{msg}</p>}
        </div>
    );
}

export default function PhoneNotificationsPage({ user }) {
    const api = useApiClient();
    const [cfg, setCfg] = useState(null);
    const [devices, setDevices] = useState([]);
    const [prefs, setPrefs] = useState({});
    const [here, setHere] = useState(null);          // this browser's subscription endpoint, or null
    const [permission, setPermission] = useState(supported() ? Notification.permission : 'unsupported');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');

    const load = useCallback(async () => {
        setErr('');
        try {
            const [c, d, p] = await Promise.all([api.get('/api/push/config'), api.get('/api/push/devices'), api.get('/api/push/preferences')]);
            setCfg(c); setDevices(d.devices || []); setPrefs(p.categories || {});
        } catch (e) { setErr(e.message); }
        if (supported()) {
            try {
                const reg = await registration();
                const sub = reg ? await reg.pushManager.getSubscription() : null;
                setHere(sub ? sub.endpoint : null);
            } catch { setHere(null); }
        }
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const run = async (fn, okText) => {
        setBusy(true); setMsg(''); setErr('');
        try { await fn(); if (okText) setMsg(okText); await load(); } catch (e) { setErr(e.message); } finally { setBusy(false); }
    };

    const turnOn = () => run(async () => {
        if (!cfg || !cfg.public_key) throw new Error((cfg && cfg.message) || 'The server is not ready for notifications yet.');
        const p = await Notification.requestPermission();
        setPermission(p);
        if (p !== 'granted') throw new Error('Notifications are blocked for this site. Allow them in the browser or phone settings, then try again.');
        const reg = await registration();
        if (!reg) throw new Error('The app\'s background service is not running on this device. Reload the page and try again.');
        let sub = await reg.pushManager.getSubscription();
        if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(cfg.public_key) });
        await api.post('/api/push/subscribe', { subscription: sub.toJSON() });
    }, 'Notifications are on for this device.');

    const turnOff = () => run(async () => {
        const reg = await registration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (sub) {
            const endpoint = sub.endpoint;
            try { await sub.unsubscribe(); } catch { /* the server row is removed anyway */ }
            await api.post('/api/push/unsubscribe', { endpoint });
        }
    }, 'Notifications are off for this device.');

    const test = (deviceId) => run(async () => {
        const r = await api.post('/api/push/test', deviceId ? { device_id: deviceId } : {});
        if (!r.sent) throw new Error(`The test did not go through${r.results && r.results[0] && r.results[0].detail ? `: ${r.results[0].detail}` : '.'}`);
    }, 'Test sent. It should appear within a few seconds.');

    const rename = (d) => {
        const label = window.prompt('Name this device', d.device_label || '');
        if (label && label.trim()) run(() => api.patch(`/api/push/devices/${d.subscription_id}`, { label: label.trim() }));
    };
    const remove = (d) => {
        if (window.confirm(`Remove "${d.device_label || 'this device'}"? It will get no more notifications.`)) {
            run(() => api.delete(`/api/push/devices/${d.subscription_id}`), 'Device removed.');
        }
    };
    const savePrefs = () => run(async () => {
        const r = await api.put('/api/push/preferences', { categories: prefs });
        setPrefs(r.categories || prefs);
    }, 'Your choices are saved.');

    return (
        <div className="max-w-3xl space-y-5">
            <div>
                <h1 className="text-2xl font-bold text-slate-800">Phone Notifications</h1>
                <p className="text-sm text-slate-600 mt-1">
                    Get a notification on your phone or computer when something needs you: a new lead, a showing request, a TC item
                    due, a payment received, a drip-campaign reply or a website chat hand-off. The notification shows only a short
                    summary; tap it to open the record in the portal.
                </p>
            </div>

            {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">{err}</div>}
            {msg && <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-md p-3">{msg}</div>}
            {cfg && !cfg.enabled && (
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
                    The managing broker has not switched automatic phone notifications on for the office yet. You can turn this
                    device on now and send yourself a test; event notifications start when the office switch is on.
                </div>
            )}

            {user && user.isManagingBroker && <OfficeSwitch api={api} />}

            <div className={card}>
                <h2 className="text-lg font-semibold text-slate-800">This device</h2>
                {!supported() ? (
                    <p className="text-sm text-slate-600">
                        {isIos() && !standalone()
                            ? 'On iPhone and iPad, notifications work only from the installed app: tap Share → Add to Home Screen, open BREC from the home screen, then come back to this page.'
                            : 'This browser cannot receive push notifications. Use Chrome, Edge, Firefox or Safari (or the BREC app on Android).'}
                    </p>
                ) : (
                    <div className="space-y-2">
                        <p className="text-sm text-slate-700">
                            Status: {here ? <strong className="text-green-700">on</strong> : <strong className="text-slate-500">off</strong>}
                            {permission === 'denied' && <span className="text-red-700"> — notifications are blocked in this browser's settings</span>}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {here
                                ? <button className={ghost} disabled={busy} onClick={turnOff}>Turn off on this device</button>
                                : <button className={btn} disabled={busy || !cfg} onClick={turnOn}>Turn on for this device</button>}
                            <button className={ghost} disabled={busy || devices.length === 0} onClick={() => test(null)}>Send a test to all my devices</button>
                        </div>
                    </div>
                )}
            </div>

            <div className={card}>
                <h2 className="text-lg font-semibold text-slate-800">My devices</h2>
                {devices.length === 0 ? (
                    <p className="text-sm text-slate-600">No device has notifications turned on for your login yet.</p>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {devices.map((d) => (
                            <li key={d.subscription_id} className="py-2 flex flex-wrap items-center justify-between gap-2">
                                <div className="text-sm">
                                    <div className="font-medium text-slate-800">{d.device_label || 'Device'}</div>
                                    <div className="text-xs text-slate-500">
                                        Added {fmt(d.created_at)} · last delivered {fmt(d.last_success_at)}
                                        {d.last_error ? ` · last problem: ${d.last_error}` : ''}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button className={ghost} disabled={busy} onClick={() => test(d.subscription_id)}>Test</button>
                                    <button className={ghost} disabled={busy} onClick={() => rename(d)}>Rename</button>
                                    <button className={ghost} disabled={busy} onClick={() => remove(d)}>Remove</button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className={card}>
                <h2 className="text-lg font-semibold text-slate-800">What to notify me about</h2>
                <p className="text-xs text-slate-500">Applies to all your devices. Email and text alerts have their own settings.</p>
                <div className="space-y-2">
                    {(cfg ? cfg.categories : []).map((c) => (
                        <label key={c.key} className="flex items-start gap-2 text-sm">
                            <input type="checkbox" className="mt-1" checked={prefs[c.key] !== false}
                                onChange={(e) => setPrefs({ ...prefs, [c.key]: e.target.checked })} />
                            <span><span className="font-medium text-slate-800">{c.label}</span>
                                <span className="block text-xs text-slate-500">{c.help}</span></span>
                        </label>
                    ))}
                </div>
                <button className={btn} disabled={busy || !cfg} onClick={savePrefs}>Save my choices</button>
            </div>
        </div>
    );
}
